/**
 * AskClaude.tsx · 09_APP · OS-006 · 2026-10-20 · C45, v63, D-0074
 *
 * Her words: "up on top somewhere would be a microphone for Claude... so I know that I could say like, oh, and
 * talk with Claude about this contact there. That kind of needs to be available almost like everywhere on the
 * site... You're the assistant. You just do it."
 *
 * One bar under the tabs, on every page. Mic or typing. On a profile it carries that person, so "send her the
 * Academy dates" needs no name. Honest about today: requests are SAVED with page and person and Claude works the
 * list at the start of every session; live replies arrive with the C41(i) engine, reading this same table.
 */

import { useEffect, useState, useSyncExternalStore } from "react";
import { os, type AssistantInbox } from "../lib/os";
import { useDictation } from "../lib/speech";
import { parseHeadcount, soundsLikeOutreach } from "../lib/outreach_parse";
import { announceDueChanged, announceImpact } from "./ComingUp";
import "../profile.css";

// --- what is on screen right now: set by a page, read by the bar ---------------------------------------
type Ctx = { page: string; personId: string | null; personName: string | null };
let ctx: Ctx = { page: "today", personId: null, personName: null };
const subs = new Set<() => void>();
export function setAskContext(next: Partial<Ctx>) {
  ctx = { ...ctx, ...next };
  subs.forEach((f) => f());
}
const useAskContext = () => useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => ctx);

export function AskClaude({ suggestions, placeholder: fixed }: { suggestions?: string[]; placeholder?: string }) {
  const c = useAskContext();
  const [text, setText] = useState("");
  const [inbox, setInbox] = useState<AssistantInbox | null>(null);
  const [showList, setShowList] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // C51: "I just did outreach and served 18 women" becomes a confirm chip, never a silent write.
  const [offer, setOffer] = useState<{ said: string; n: number; place: string; on: string; calId: string | null } | null>(null);
  const mic = useDictation();

  useEffect(() => { os.assistantInbox().then(setInbox).catch(() => { /* bar still works */ }); }, []);

  const first = c.personName?.split(" ")[0] ?? null;
  const placeholder = fixed ?? (first ? `Ask Claude about ${first}, or tell it what to do` : "Ask Claude, or tell it what to do");

  async function send(said: string, skipOutreach = false) {
    const t = said.trim();
    if (!t || busy) return;
    const n = parseHeadcount(t);
    if (!skipOutreach && n !== null && soundsLikeOutreach(t)) {
      // 2026-10-21: she said "22 women" from the car and it was lost, because this
      // path used to wait for a tap before saving anything. Her words are saved FIRST,
      // always; the "Log it" card is only a shortcut on top. Never undo this order.
      setBusy(true);
      try {
        setInbox(await os.assistantAsk(t, c.page, c.personId));
        setText("");
        setMsg("Saved for Claude. Tap Log it to count it now.");
      } catch (e) {
        setMsg(e instanceof Error ? e.message : String(e));
        setBusy(false);
        return;
      }
      setBusy(false);
      setOffer({ said: t, n, ...(await outreachTarget()) });
      return;
    }
    setBusy(true);
    try {
      setInbox(await os.assistantAsk(t, c.page, c.personId));
      setText("");
      setMsg(first ? `Saved for Claude, about ${first}.` : "Saved for Claude.");
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      window.setTimeout(() => setMsg(null), 4000);
    }
  }

  async function logOffer() {
    if (!offer || busy || !offer.place.trim()) return;
    setBusy(true);
    try {
      const impact = await os.outreachLog(offer.place.trim(), offer.n, offer.on, "talk", false, offer.calId);
      announceImpact(impact);
      announceDueChanged();
      setMsg(`Logged: ${offer.n} women at ${offer.place.trim()}. It is in Women reached.`);
      setOffer(null);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      window.setTimeout(() => setMsg(null), 5000);
    }
  }

  const waiting = inbox?.waiting.length ?? 0;

  return (
    <div className="askc">
      <div className="askc-box">
        <input value={mic.listening ? mic.live : text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void send(text); }}
          placeholder={mic.listening ? "Listening… tap the mic when you are done" : placeholder}
          aria-label="Ask Claude" readOnly={mic.listening} />
        {mic.supported && (
          <button className={mic.listening ? "askc-mic on" : "askc-mic"} aria-label={mic.listening ? "Stop and save" : "Talk to Claude"}
            title={mic.listening ? "Tap to stop" : "Talk to Claude"}
            onClick={() => mic.toggle((said) => void send(said))}>&#127908;</button>
        )}
        <button className="askc-send" disabled={busy || !text.trim()} onClick={() => void send(text)}>Ask</button>
      </div>
      {suggestions && (
        <div className="sugg">{suggestions.map((x) => <button key={x} onClick={() => setText(x)}>{x}</button>)}</div>
      )}
      {offer && (
        <div className="askc-offer" role="group" aria-label="Log this outreach">
          <p>Log <b>{offer.n} women</b> {offer.calId ? <>at <b>{offer.place}</b></> : "at"} {offer.on === todayLA() ? "today" : offer.on}?</p>
          {!offer.calId && <input className="ref-in" value={offer.place} placeholder="Where was it?"
            onChange={(e) => setOffer({ ...offer, place: e.target.value })} />}
          <div className="askc-offer-b">
            <button className="ref-save" disabled={busy || !offer.place.trim()} onClick={() => void logOffer()}>Log it</button>
            <button className="oask-no" disabled={busy} onClick={() => { setOffer(null); setMsg("Saved for Claude."); window.setTimeout(() => setMsg(null), 4000); }}>No, Claude has it</button>
          </div>
        </div>
      )}
      {mic.error && <p className="askc-note">{mic.error}</p>}
      {msg && <p className="askc-note" role="status">{msg}</p>}
      {waiting > 0 && (
        <button className="askc-count" onClick={() => setShowList((s) => !s)}>
          {waiting} waiting for Claude {showList ? "▴" : "▾"}
        </button>
      )}
      {showList && inbox && (
        <ul className="askc-list">
          {inbox.waiting.map((w) => (
            <li key={w.id}><span>{new Date(w.at).toLocaleString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}{w.person ? ` · ${w.person}` : ""}</span>{w.said}</li>
          ))}
          <li className="askc-how">Claude works this list at the start of every session. Live answers here come with the assistant engine.</li>
        </ul>
      )}
    </div>
  );
}

const todayLA = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });

/** Which talk she means: the outreach still waiting for a number, else today's outreach on her calendar. */
async function outreachTarget(): Promise<{ place: string; on: string; calId: string | null }> {
  try {
    const due = await os.outreachDue();
    const d = due[0];
    if (d) {
      return { place: d.place, on: new Date(d.starts_at).toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" }), calId: d.id };
    }
    const today = todayLA();
    const cal = (await os.comingUp(1)).find((c) => c.is_outreach
      && new Date(c.starts_at).toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" }) === today);
    if (cal) return { place: cal.location ?? cal.title, on: today, calId: cal.id };
  } catch { /* fall through: she types the place */ }
  return { place: "", on: todayLA(), calId: null };
}
